import { Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FeedPost } from '@up2d8/contracts';

/**
 * De vertaalregels boven een bericht: automatisch vertaald (met "Origineel tonen"), het origineel
 * staat er (met "Vertaling tonen"), of de vertaling kwam niet.
 *
 * Automatisch vertaald, en dat staat er. Een medewerker die niet weet dat een machine zijn
 * instructie vertaalde, kan een rare zin niet plaatsen — en bij een instructie is "ik dacht dat het
 * zo bedoeld was" het slechtste dat kan gebeuren. Daarom: wat het is, waaruit het komt, en één tik
 * naar het origineel (ADR-0043).
 *
 * Eén component voor de telefoon en het bureauscherm. Eerst stonden de regels alleen in de
 * telefoonweergave en kreeg het bureaudetail ze niet door (testrapport 28 okt., #12).
 *
 * Geeft `null` als er niets te melden is, zodat de plaats eromheen niet leeg blijft staan.
 */
export function PostTranslationNotice({
  post,
  showOriginal,
  onShowOriginal,
}: {
  post: FeedPost;
  showOriginal: boolean;
  onShowOriginal: (value: boolean) => void;
}) {
  const { t } = useTranslation();

  if (!post.isTranslated && !showOriginal && !post.translationUnavailable) return null;

  return (
    <div data-testid="translation-notice" className="flex flex-col gap-element">
      {post.isTranslated && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-background px-3 py-2">
          <Languages aria-hidden className="h-4 w-4 shrink-0 text-text-muted" />
          <span className="text-small text-text-secondary">
            {t('translation.machineTranslated', {
              language: t(`languages.${post.sourceLanguage}`),
            })}
          </span>
          <button
            type="button"
            onClick={() => onShowOriginal(true)}
            className="text-small text-brand-primary underline"
          >
            {t('translation.showOriginal')}
          </button>
        </div>
      )}

      {showOriginal && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-background px-3 py-2">
          <Languages aria-hidden className="h-4 w-4 shrink-0 text-text-muted" />
          <span className="text-small text-text-secondary">{t('translation.showingOriginal')}</span>
          <button
            type="button"
            onClick={() => onShowOriginal(false)}
            className="text-small text-brand-primary underline"
          >
            {t('translation.showTranslation')}
          </button>
        </div>
      )}

      {/*
        De vertaling kwam niet. Dat staat er ook, want het verschil tussen "dit bericht is
        Nederlands" en "de vertaling is stuk" bepaalt of iemand wacht of een collega vraagt.
      */}
      {post.translationUnavailable && (
        <p className="rounded-lg bg-warning-tint px-3 py-2 text-small text-text-primary">
          {t('translation.unavailable')}
        </p>
      )}
    </div>
  );
}
